import type { BundleStripSlice } from '@adapters/prismic/types/brandedStorePage';
import type { BundlePromotion } from '@adapters/bigcommerce/transforms/bundlePromotion';
import type { HitProps } from '@components/algolia/Hit';

export const BUNDLE_STRIP_MAX_PRODUCTS = 4;
export const BUNDLE_STRIP_MIN_PRODUCTS = 2;

export interface BundleStripImage {
  url: string;
  alt: string | null;
}

export interface BundleStripProduct {
  product: HitProps;
  variantSku: string | null;
}

export interface BundleStripLink {
  label: string | null;
  url: string;
}

export interface BundleStripData {
  heading: string;
  description: string | null;
  badgeLabel: string | null;
  image: BundleStripImage | null;
  ctaLabel: string | null;
  buildYourOwn: BundleStripLink | null;
  coOpBrand: string | null;
  products: BundleStripProduct[];
  promotion: BundlePromotion | null;
}

type BundleStripSliceLike = {
  primary?: BundleStripSlice['primary'] | null;
  items?: BundleStripSlice['items'] | null;
};

const trimmed = (value: unknown): string | null =>
  typeof value === 'string' ? value.trim() || null : null;

const authoredItems = (
  slice: BundleStripSliceLike | null | undefined
): { sku: string; variantSku: string | null }[] =>
  (slice?.items ?? [])
    .flatMap((item) => {
      const sku = trimmed(item?.product_sku);
      return sku ? [{ sku, variantSku: trimmed(item?.variant_sku) }] : [];
    })
    .slice(0, BUNDLE_STRIP_MAX_PRODUCTS);

export const collectBundleStripPromotionId = (
  slice: BundleStripSliceLike | null | undefined
): number | null => {
  const id = Number(slice?.primary?.promotion_id);
  return Number.isInteger(id) && id > 0 ? id : null;
};

export const collectBundleStripSkus = (
  slice: BundleStripSliceLike | null | undefined
): string[] => authoredItems(slice).map(({ sku }) => sku);

export const collectBundleStripVariantSkus = (
  slice: BundleStripSliceLike | null | undefined
): string[] =>
  authoredItems(slice).flatMap(({ variantSku }) =>
    variantSku ? [variantSku] : []
  );

export const transformBundleStrip = (
  slice: BundleStripSliceLike | null | undefined,
  resolvedProducts: Record<string, HitProps> = {},
  resolvedPromotions: Record<number, BundlePromotion> = {}
): BundleStripData | null => {
  const primary = slice?.primary;
  const heading = trimmed(primary?.title);
  if (!primary || !heading) return null;

  const seen = new Set<string>();
  const products: BundleStripProduct[] = [];

  for (const { sku, variantSku } of authoredItems(slice)) {
    const product = resolvedProducts[sku];
    if (!product || product.availableOnPlp === false) return null;
    if (!seen.has(product.parentSku)) {
      seen.add(product.parentSku);
      products.push({ product, variantSku });
    }
  }

  if (products.length < BUNDLE_STRIP_MIN_PRODUCTS) return null;

  const promotionId = collectBundleStripPromotionId(slice);
  const imageUrl = trimmed(primary.bundle_image?.url);
  const linkUrl = trimmed(primary.build_your_own_link);

  return {
    heading,
    description: trimmed(primary.description),
    badgeLabel: trimmed(primary.badge_label),
    image: imageUrl
      ? { url: imageUrl, alt: trimmed(primary.bundle_image?.alt) }
      : null,
    ctaLabel: trimmed(primary.cta_label),
    buildYourOwn: linkUrl
      ? { label: trimmed(primary.build_your_own_label), url: linkUrl }
      : null,
    coOpBrand: trimmed(primary.co_op_brand),
    products,
    promotion: (promotionId && resolvedPromotions[promotionId]) || null,
  };
};
